import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, addDays } from 'date-fns';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkFrontmatter from 'remark-frontmatter';
import { cn } from '@/lib/utils';
import {
  FileText,
  Building2,
  Package,
  MessageSquare,
  ExternalLink,
  Printer,
  Plus,
  Trash2,
  Paperclip,
  Upload,
  Search,
  Loader2,
  Unlink,
  Radio,
  RefreshCw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { SearchableSelect } from '@features/operations-transmitters/components/shared/SearchableSelect';
import { InvoiceStatusBadge } from '../InvoiceStatusBadge';
import {
  useCustomerOrganisations,
  useSubscriptionsByCustomer,
  useOrganisationUsers,
  useQuotesByAccount,
  useProjectsByAccount,
  useInvoiceSensors,
  useAddSensorToInvoice,
  useRemoveSensorFromInvoice,
  useAddInvoiceNote,
  useContractById,
  useUploadInvoicePoFile,
  useDeleteInvoicePoFile,
} from '../../hooks';
import type { ContractDoc } from '../../hooks';
import { useSession } from '@/hooks/useSession';
import { useSite } from '@/features/sites/hooks';
import { SensorSelectDialog } from '@/features/quotes/components/sensors/SensorSelectDialog';
import { InvoiceSensorsTable } from '../sensors/InvoiceSensorsTable';
import type { QuoteSensor } from '@/features/quotes/types';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import { useCatalogSkuSearch } from '@features/wms/hooks/useWmsSkus';
import { useInvoiceDocuments, useAddInvoiceDocument, useRemoveInvoiceDocument } from '../../hooks';
import {
  searchDocuments,
  getUploadUrl,
  uploadToSignedUrl,
  confirmUpload,
  computeFileChecksum,
  checkDuplicate,
} from '@/features/documents/api';
import { useDocumentDetail, useDocumentPreviewUrl } from '@/features/documents/hooks/useDocuments';
import type { SkuSearchResult } from '@features/wms/types';
import type {
  Invoice,
  InvoiceSku,
  InvoiceSubtotal,
  UpdateInvoiceInput,
  InvoiceStatus,
  PaymentTerms,
  TaxCategory,
  Currency,
  Language,
} from '../../types';
import {
  getContractDisplay,
  getUserName,
  isPopulatedUser,
  isPopulatedSite,
} from '../../types';
import { TAX_CATEGORIES, provinceToTaxCategory, normalizeProvince } from '@atek/ca-tax';
import { TAX_CATEGORY_OPTIONS } from '../../types';
import { buildInvoiceFileName } from '../../utils/printInvoice';

interface InvoiceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  onSubmit: (data: UpdateInvoiceInput) => Promise<void>;
  onViewHtml?: (invoiceId: string, fileName: string) => void;
  isSubmitting: boolean;
}

function formatDateValue(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

function formatCurrency(amount: number | undefined, currency: string = 'CAD'): string {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd HH:mm');
  } catch {
    return '-';
  }
}

function extractId(ref: string | { _id: string } | undefined): string {
  if (!ref) return '';
  if (typeof ref === 'string') return ref;
  return ref._id;
}

function calculateLineTotal(qty: number, unitPrice: number, discount?: number): number {
  const d = discount || 0;
  return qty * unitPrice * (1 - d / 100);
}

function roundAmount(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function buildUpdatedSubtotals(
  skus: InvoiceSku[],
  existingSubtotals: InvoiceSubtotal[] = []
): InvoiceSubtotal[] {
  if (skus.length === 0) return [];

  const fallbackSubtotalId = existingSubtotals[0]?._id || 'subtotal';
  const fallbackDisplayName = existingSubtotals[0]?.displayName || 'Subtotal';
  const groupedTotals = new Map<string, InvoiceSubtotal>();

  for (const subtotal of existingSubtotals) {
    const subtotalId = subtotal._id || fallbackSubtotalId;
    groupedTotals.set(subtotalId, {
      _id: subtotalId,
      displayName: subtotal.displayName || fallbackDisplayName,
      total: 0,
    });
  }

  for (const sku of skus) {
    const subtotalId = sku.subtotal_id || fallbackSubtotalId;
    const existingSubtotal = groupedTotals.get(subtotalId) || {
      _id: subtotalId,
      displayName: fallbackDisplayName,
      total: 0,
    };
    existingSubtotal.total += calculateLineTotal(sku.quantity, sku.unit_price, sku.discount);
    groupedTotals.set(subtotalId, existingSubtotal);
  }

  return Array.from(groupedTotals.values())
    .filter((subtotal) => subtotal.total > 0)
    .map((subtotal) => ({
      ...subtotal,
      total: roundAmount(subtotal.total),
    }));
}

const statusOptions: InvoiceStatus[] = ['preparation', 'sent', 'paid', 'cancelled'];
const paymentTermsOptions: { value: PaymentTerms; label: string }[] = [
  { value: 'on-order', label: 'On Order' },
  { value: 'net-0', label: 'Net 0' },
  { value: 'net-30', label: 'Net 30' },
  { value: 'net-45', label: 'Net 45' },
  { value: 'net-60', label: 'Net 60' },
];
const taxCategoryOptions = TAX_CATEGORY_OPTIONS;

// =============================================================================
// Document Row — shows a single linked document with preview/unlink
// =============================================================================

function DocumentRow({ documentId, onUnlink, isUnlinking }: {
  documentId: string;
  onUnlink: () => void;
  isUnlinking: boolean;
}) {
  const detailQuery = useDocumentDetail(documentId);
  const previewQuery = useDocumentPreviewUrl(documentId, !!detailQuery.data);
  const doc = detailQuery.data;
  const previewUrl = previewQuery.data?.previewUrl;
  const mimeType = previewQuery.data?.mimeType || doc?.currentVersion?.sourceMimeType || '';
  const content = doc?.currentVersion?.content || '';
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const isHtml = mimeType === 'text/html';
  const isTextLike =
    !isImage && !isPdf && !isHtml
    && (
      mimeType === 'text/markdown'
      || mimeType === 'text/plain'
      || (!!content && !mimeType)
    );

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm font-medium truncate">
            {detailQuery.isLoading ? 'Loading...' : doc?.name || 'Unknown document'}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {previewUrl && !isTextLike && !isHtml && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary p-1"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <button
            type="button"
            onClick={() => { if (window.confirm('Unlink this document from the invoice?')) onUnlink(); }}
            disabled={isUnlinking}
            className="text-muted-foreground hover:text-destructive p-1"
            title="Unlink document"
          >
            <Unlink className="h-4 w-4" />
          </button>
        </div>
      </div>
      {previewUrl && isImage && (
        <div className="px-3 pb-3">
          <img src={previewUrl} alt={doc?.name || 'Preview'} className="w-full rounded border object-contain max-h-48" />
        </div>
      )}
      {previewUrl && isPdf && (
        <div className="px-3 pb-3">
          <iframe src={previewUrl} title={doc?.name || 'Preview'} className="w-full h-64 rounded border" />
        </div>
      )}
      {previewUrl && isHtml && (
        <div className="px-3 pb-3">
          <iframe
            src={previewUrl}
            title={doc?.name || 'Preview'}
            className="w-full h-96 rounded border bg-white"
            sandbox="allow-same-origin allow-popups"
          />
        </div>
      )}
      {isTextLike && content && (
        <div className="px-3 pb-3">
          <div className="prose prose-sm max-w-none border rounded p-3 bg-muted/30 max-h-96 overflow-y-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkFrontmatter]}>
              {content}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Documents Tab — upload, pick from DMS, list linked documents
// =============================================================================

function InvoiceDocumentsTab({ invoiceId }: { invoiceId: string }) {
  const [mode, setMode] = useState<'list' | 'search'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const documentsQuery = useInvoiceDocuments(invoiceId);
  const addDocMutation = useAddInvoiceDocument();
  const removeDocMutation = useRemoveInvoiceDocument();

  const documents = documentsQuery.data || [];

  // Debounced DMS search
  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const results = await searchDocuments(query);
      setSearchResults(results.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const processFile = async (file: File) => {
    setIsUploading(true);
    try {
      const checksum = await computeFileChecksum(file);
      const { duplicates } = await checkDuplicate(checksum);

      if (duplicates.length > 0) {
        await addDocMutation.mutateAsync({ invoiceId, documentId: duplicates[0].documentId });
      } else {
        const { documentId, uploadUrl } = await getUploadUrl(file.name, file.type, { uploadSource: 'ui' });
        await uploadToSignedUrl(uploadUrl, file);
        await confirmUpload(documentId, file.size, checksum);
        await addDocMutation.mutateAsync({ invoiceId, documentId });
      }
      setMode('list');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  };

  // Link existing DMS document
  const handleLinkDocument = async (documentId: string) => {
    await addDocMutation.mutateAsync({ invoiceId, documentId });
    setSearchQuery('');
    setSearchResults([]);
    setMode('list');
  };

  const linkedDocIds = new Set(documents.map((d) => d.documentId));

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <label
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          isUploading && 'pointer-events-none opacity-60',
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input type="file" className="hidden" onChange={handleUpload} disabled={isUploading} />
        {isUploading ? (
          <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground" />
        )}
        <div className="text-center">
          <p className="text-sm font-medium">{isUploading ? 'Uploading...' : 'Drop file here or click to upload'}</p>
          <p className="text-xs text-muted-foreground mt-0.5">PDF, images, or any document</p>
        </div>
      </label>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => setMode(mode === 'search' ? 'list' : 'search')}
      >
        <Search className="h-4 w-4 mr-1" />
        {mode === 'search' ? 'Cancel search' : 'Pick from DMS'}
      </Button>

      {/* DMS Search */}
      {mode === 'search' && (
        <div className="space-y-2">
          <Input
            placeholder="Search documents in DMS..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
          {isSearching && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
              {searchResults.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => handleLinkDocument(doc.id)}
                  disabled={linkedDocIds.has(doc.id) || addDocMutation.isPending}
                  className="w-full text-left flex items-center justify-between p-2 rounded hover:bg-muted text-sm disabled:opacity-50"
                >
                  <span className="truncate min-w-0 flex-1">{doc.name}</span>
                  {linkedDocIds.has(doc.id) ? (
                    <span className="text-xs text-muted-foreground ml-2">Linked</span>
                  ) : (
                    <Plus className="h-4 w-4 ml-2 shrink-0 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          )}
          {searchQuery && !isSearching && searchResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">No documents found</p>
          )}
        </div>
      )}

      {/* Linked Documents List */}
      <div className="space-y-2">
        <Label>Linked Documents ({documents.length})</Label>
        {documentsQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center text-muted-foreground py-6 text-sm border rounded-lg">
            No documents linked yet
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                documentId={doc.documentId}
                onUnlink={() => removeDocMutation.mutate({ invoiceId, documentId: doc.documentId })}
                isUnlinking={removeDocMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Form section (color-coded header, matching the quote form's section accents
// so repeated sections share the same colour: details=green, customer=blue,
// items=indigo, sensors=teal, notes=slate)
// =============================================================================

function InvoiceFormSection({
  title,
  icon,
  accent,
  children,
  className,
}: {
  title: string;
  icon: ReactNode;
  accent: 'blue' | 'green' | 'amber' | 'indigo' | 'teal' | 'slate' | 'purple';
  children: ReactNode;
  className?: string;
}) {
  const accentClasses = {
    blue: 'border-sky-200/80 [&>div:first-child]:border-sky-200/80 [&>div:first-child]:bg-sky-50/60',
    green: 'border-emerald-200/80 [&>div:first-child]:border-emerald-200/80 [&>div:first-child]:bg-emerald-50/60',
    amber: 'border-amber-200/80 [&>div:first-child]:border-amber-200/80 [&>div:first-child]:bg-amber-50/60',
    indigo: 'border-indigo-200/80 [&>div:first-child]:border-indigo-200/80 [&>div:first-child]:bg-indigo-50/60',
    teal: 'border-teal-200/80 [&>div:first-child]:border-teal-200/80 [&>div:first-child]:bg-teal-50/60',
    slate: 'border-slate-200/80 [&>div:first-child]:border-slate-200/80 [&>div:first-child]:bg-slate-50/70',
    purple: 'border-violet-200/80 [&>div:first-child]:border-violet-200/80 [&>div:first-child]:bg-violet-50/60',
  };

  return (
    <section className={cn('rounded-md border bg-background', accentClasses[accent], className)}>
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

// =============================================================================
// Main Invoice Form Modal
// =============================================================================

export function InvoiceFormModal({
  open,
  onOpenChange,
  invoice,
  onSubmit,
  onViewHtml,
  isSubmitting,
}: InvoiceFormModalProps) {
  const [formData, setFormData] = useState<UpdateInvoiceInput>({});
  const [sensorDialogOpen, setSensorDialogOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const addNoteMutation = useAddInvoiceNote();
  const uploadPoFileMutation = useUploadInvoicePoFile();
  const deletePoFileMutation = useDeleteInvoicePoFile();
  const poFileInputRef = useRef<HTMLInputElement>(null);
  const { user: sessionUser } = useSession();
  const { t } = useTranslation('invoices');

  // Data queries for dropdowns
  const customersQuery = useCustomerOrganisations();
  const salesManagersQuery = useSalesManagers();
  const customers = customersQuery.data || [];
  const salesManagers = salesManagersQuery.data || [];

  const invoiceCustomerId = extractId(invoice?.customer);
  const selectedCustomer = formData.customer
    ? customers.find((c) => c._id === formData.customer)
    : customers.find((c) => c._id === invoiceCustomerId || c.mongoOrgId === invoiceCustomerId);
  const effectiveCustomerId = selectedCustomer?._id || formData.customer || '';
  const mongoOrgId = selectedCustomer?.mongoOrgId || invoiceCustomerId;
  const contractsQuery = useSubscriptionsByCustomer(mongoOrgId, effectiveCustomerId || undefined);
  const orgUsersQuery = useOrganisationUsers(effectiveCustomerId);
  const quotesQuery = useQuotesByAccount(effectiveCustomerId);
  const projectsQuery = useProjectsByAccount(effectiveCustomerId);
  const contracts = contractsQuery.data || [];
  const orgUsers = orgUsersQuery.data || [];
  const quotes = quotesQuery.data || [];
  const projects = projectsQuery.data || [];

  // The invoice's populated billing_site carries only { _id, name, email } (the
  // Mongo populate deliberately selects no address), so the place-of-supply
  // check below reads the address from PostgreSQL instead. GET /v4/sites/:id
  // accepts the Mongo site id directly, which is what billing_site stores.
  const billingSiteMongoId = isPopulatedSite(invoice?.billing_site)
    ? invoice.billing_site._id
    : undefined;
  const billingSiteQuery = useSite(billingSiteMongoId);

  // Sensor hooks — keyed by invoice_number for the read, _id for the mutations
  const invoiceNumber = invoice?.invoice_number;
  const invoiceMongoId = invoice?._id;
  const { sensors, isLoading: sensorsLoading } = useInvoiceSensors(invoiceNumber, mongoOrgId);
  const addSensorMutation = useAddSensorToInvoice(invoiceNumber);
  const removeSensorMutation = useRemoveSensorFromInvoice(invoiceNumber);

  const linkedSensorIds = useMemo(
    () => new Set(sensors.map((s) => s._id)),
    [sensors],
  );

  const handleAddSensor = (sensor: QuoteSensor) => {
    if (!invoiceMongoId) return;
    addSensorMutation.mutate({ invoiceId: invoiceMongoId, sensorId: sensor._id });
  };

  const handleRemoveSensor = (sensorId: string) => {
    if (!invoiceMongoId) return;
    removeSensorMutation.mutate({ invoiceId: invoiceMongoId, sensorId });
  };

  const handleRemoveAllSensors = () => {
    if (!invoiceMongoId) return;
    sensors.forEach((s) =>
      removeSensorMutation.mutate({ invoiceId: invoiceMongoId, sensorId: s._id }),
    );
  };
  const invoiceContractId = extractId(invoice?.contract);
  const selectedContract = formData.contract !== undefined
    ? contracts.find((c) => c._id === formData.contract || c.mongoContractId === formData.contract)
    : contracts.find((c) => c._id === invoiceContractId || c.mongoContractId === invoiceContractId);
  const effectiveContractId =
    formData.contract !== undefined
      ? (formData.contract || '__none__')
      : (selectedContract?._id || '__none__');

  // Resolve the Mongo contract id for the full-contract fetch (fill-from-subscription)
  const mongoContractIdForFill = selectedContract?.mongoContractId;
  const contractDocQuery = useContractById(mongoContractIdForFill);

  // SKU catalog for dropdown
  const { data: catalogSkus } = useCatalogSkuSearch(undefined, 500);
  const skuOptions = useMemo(() => {
    if (!catalogSkus) return [];
    return catalogSkus.map((s: SkuSearchResult) => ({
      _id: s.sku,
      name: `${s.sku} — ${s.nameFr || s.nameEn || ''}`,
    }));
  }, [catalogSkus]);
  const skuLookup = useMemo(() => {
    if (!catalogSkus) return new Map<string, SkuSearchResult>();
    return new Map(catalogSkus.map((s: SkuSearchResult) => [s.sku, s]));
  }, [catalogSkus]);

  // Effective SKUs (formData overrides invoice)
  const effectiveSkus = formData.skus || invoice?.skus || [];

  // Calculate totals from effective SKUs
  const subtotal = useMemo(
    () => effectiveSkus.reduce((sum, sku) => sum + calculateLineTotal(sku.quantity, sku.unit_price, sku.discount), 0),
    [effectiveSkus]
  );

  // Reset form when invoice changes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData({});
    }
    onOpenChange(newOpen);
  };

  // R7/V1 parity: expiration_date is always auto-calculated from date + payment_terms.
  // Recalc whenever either changes. net-0 / on-order → 0 days; net-30/45/60 → N days.
  useEffect(() => {
    if (!invoice) return;
    const dateStr = formData.date || invoice.date;
    const terms = formData.payment_terms || invoice.payment_terms;
    if (!dateStr || !terms) return;
    const daysMap: Record<string, number> = {
      'on-order': 0,
      'net-0': 0,
      'net-30': 30,
      'net-45': 45,
      'net-60': 60,
    };
    const days = daysMap[terms] ?? 0;
    try {
      const base = parseISO(dateStr);
      const calculated = format(addDays(base, days), 'yyyy-MM-dd');
      setFormData((prev) => ({ ...prev, expiration_date: calculated }));
    } catch {
      // ignore malformed date
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date, formData.payment_terms, invoice?.date, invoice?.payment_terms]);

  if (!invoice) return null;

  // R7: lock all non-allowlist fields once invoice leaves preparation status.
  // Mirrors V1 disableFormByStatus: locked only when BOTH the in-session
  // selection AND the originally-loaded status are non-preparation.
  const effectiveStatus = formData.status || invoice.status;
  const formLocked = effectiveStatus !== 'preparation' && invoice.status !== 'preparation';

  const effectiveCurrency = formData.currency || invoice.currency || 'CAD';
  const effectiveTaxCategory = (formData.tax_category || invoice.tax_category || 'none') as TaxCategory;
  const taxRate = TAX_CATEGORIES[effectiveTaxCategory].ratePct / 100;
  const taxAmount = subtotal * taxRate;
  const totalWithTax = subtotal + taxAmount;

  // Place-of-supply check: warn when the chosen tax doesn't match the customer's
  // province. DND (account 0063 / org_num 63) is GST-only regardless of province.
  // `stateProvince` is free text ("QC", "Québec", "QC - Québec", US states, …),
  // so it must be normalized — comparing it raw against a list of 2-letter codes
  // ignores most real records. normalizeProvince returns null rather than guess,
  // and a null destination means no warning.
  const destProvince = normalizeProvince(billingSiteQuery.data?.stateProvince);
  const expectedTaxCategory: TaxCategory | null =
    selectedCustomer?.org_num === 63
      ? 'GST'
      : destProvince
        ? provinceToTaxCategory(destProvince)
        : null;
  const taxMismatch = expectedTaxCategory != null && expectedTaxCategory !== effectiveTaxCategory;

  // Check if there are changes
  const hasChanges = Object.keys(formData).length > 0;

  // Invoice number is valid when it is either untouched or matches exactly 4 digits
  const invoiceNumberValid =
    formData.invoice_number === undefined || /^\d{4}$/.test(formData.invoice_number);

  /**
   * Resolve all formData references to their Mongo IDs and compute subtotals/total.
   * Returns `null` when a required reference cannot be resolved (a toast is already shown).
   */
  const buildPayload = (): UpdateInvoiceInput | null => {
    const payload: UpdateInvoiceInput = { ...formData };

    if (formData.customer) {
      const resolvedCustomer = customers.find((c) => c._id === formData.customer)?.mongoOrgId;
      if (!resolvedCustomer) {
        toast.error('Failed to resolve the selected account for invoice update');
        return null;
      }
      payload.customer = resolvedCustomer;
    }

    if (formData.contract) {
      const resolvedContract = contracts.find((c) => c._id === formData.contract)?.mongoContractId;
      if (!resolvedContract) {
        toast.error('Failed to resolve the selected subscription for invoice update');
        return null;
      }
      payload.contract = resolvedContract;
    } else if ('contract' in formData) {
      payload.contract = null;
    }

    if ('contractual_manager' in formData && !formData.contractual_manager) {
      payload.contractual_manager = null;
    }

    if (formData.skus !== undefined || formData.tax_category !== undefined) {
      payload.subtotals = buildUpdatedSubtotals(effectiveSkus, invoice!.subtotals || []);
      payload.total = roundAmount(subtotal);
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (!hasChanges) {
      handleOpenChange(false);
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    await onSubmit(payload);
    handleOpenChange(false);
  };

  /**
   * Save (if there are unsaved changes and the invoice is not paid), then print.
   * Does NOT close the modal after saving — only a deliberate "Save Changes" / "Close"
   * should do that.
   */
  const handlePrint = async () => {
    const effectiveStatus = formData.status || invoice!.status;
    if (hasChanges && effectiveStatus !== 'paid') {
      const payload = buildPayload();
      if (!payload) return;
      try {
        await onSubmit(payload);
      } catch {
        // The caller's onError toast already fired; do not proceed to print.
        return;
      }
    }

    const mergedInvoice = {
      ...invoice!,
      ...formData,
      invoice_version: formData.invoice_version != null
        ? Number(formData.invoice_version)
        : invoice!.invoice_version,
    };
    const fileName = buildInvoiceFileName(mergedInvoice);
    onViewHtml?.(invoice!._id, fileName);
  };

  const updateField = <K extends keyof UpdateInvoiceInput>(
    field: K,
    value: UpdateInvoiceInput[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (newCustomerId: string) => {
    setFormData((prev) => ({
      ...prev,
      customer: newCustomerId,
      contract: '',
      contractual_manager: '',
      quote_number: '',
      project_number: '',
    }));
  };

  /**
   * Populate form fields from the full Mongo contract doc.
   * Mirrors the V1 `fillInvoiceFromContract` behaviour.
   * - customer: resolved to PG account id by matching mongoOrgId in the loaded customers list;
   *   skipped if no match (keeps current customer).
   * - sales_manager, contractual_managers[0]: Mongo user ids passed through as-is.
   * - payment_terms, language, currency, pricelist: scalar values set directly.
   */
  const fillFromContract = (contract: ContractDoc) => {
    // Resolve Mongo customer id → PG account id so buildPayload can find it
    const resolvedPgCustomer = contract.customer
      ? (customers.find((c) => c.mongoOrgId === contract.customer)?._id ?? undefined)
      : undefined;

    setFormData((prev) => ({
      ...prev,
      ...(resolvedPgCustomer ? { customer: resolvedPgCustomer } : {}),
      ...(contract.sales_manager ? { sales_manager: contract.sales_manager } : {}),
      ...(contract.payment_terms ? { payment_terms: contract.payment_terms as PaymentTerms } : {}),
      ...(contract.contractual_managers?.[0] ? { contractual_manager: contract.contractual_managers[0] } : {}),
      ...(contract.pricelists?.[0] ? { pricelist: contract.pricelists[0] } : {}),
      ...(contract.language ? { language: contract.language as Language } : {}),
      ...(contract.currency ? { currency: contract.currency as Currency } : {}),
    }));
  };

  // SKU editing helpers
  const updateSku = (index: number, updates: Partial<InvoiceSku>) => {
    const currentSkus = [...(formData.skus || invoice.skus || [])];
    const updatedSku = { ...currentSkus[index], ...updates };
    // Recalculate total
    updatedSku.total = calculateLineTotal(updatedSku.quantity, updatedSku.unit_price, updatedSku.discount);
    currentSkus[index] = updatedSku;
    updateField('skus', currentSkus);
  };

  const addSku = () => {
    const currentSkus = [...(formData.skus || invoice.skus || [])];
    currentSkus.push({ sku: '', description: '', quantity: 1, unit_price: 0, total: 0 });
    updateField('skus', currentSkus);
  };

  const removeSku = (index: number) => {
    const currentSkus = [...(formData.skus || invoice.skus || [])];
    currentSkus.splice(index, 1);
    updateField('skus', currentSkus);
  };

  const handleSkuSelect = (index: number, skuCode: string | null) => {
    if (!skuCode) {
      updateSku(index, { sku: '', description: '', unit_price: 0 });
      return;
    }
    const catalogSku = skuLookup.get(skuCode);
    const effectiveLang = formData.language || invoice.language || 'fr';
    if (catalogSku) {
      const desc = effectiveLang === 'en'
        ? (catalogSku.nameEn || catalogSku.nameFr)
        : (catalogSku.nameFr || catalogSku.nameEn);
      updateSku(index, {
        sku: skuCode,
        description: desc,
        unit_price: catalogSku.value ? Number(catalogSku.value) : 0,
      });
    } else {
      updateSku(index, { sku: skuCode });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Fixed viewport-relative size — `h-[90vh]` (not `max-h-`) so the modal
          shell stays the same height regardless of which section is in view or
          how much content the active tab renders. */}
      <DialogContent className="w-[98vw] max-w-[98vw] sm:max-w-7xl xl:max-w-[92rem] h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="flex items-center gap-3">
              <span>Invoice #{invoice.invoice_number}</span>
              <InvoiceStatusBadge status={formData.status || invoice.status} />
            </DialogTitle>
            <div className="flex items-center gap-2">
              {onViewHtml && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { void handlePrint(); }}
                  title="View/Print Invoice"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print
                </Button>
              )}
              {invoice.hubspot_link && (
                <Button variant="outline" size="sm" asChild>
                  <a href={invoice.hubspot_link} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    HubSpot
                  </a>
                </Button>
              )}
              {(!!invoice._id && (formData.status ?? invoice.status) !== 'cancelled' && (formData.status ?? invoice.status) !== 'paid') && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Cancel this invoice?')) {
                      void onSubmit({ status: 'cancelled' })
                        .then(() => handleOpenChange(false))
                        .catch(() => {
                          /* parent mutation's onError already toasts */
                        });
                    }
                  }}
                >
                  Cancel Invoice
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Single-page layout: all sections stacked in one scrollable column. */}
          <div className="space-y-6 px-1 pb-2">

            {/* Details */}
            <InvoiceFormSection title="Details" icon={<FileText className="h-4 w-4" />} accent="green">
              <div className="grid grid-cols-2 gap-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={formData.status || invoice.status}
                    onValueChange={(v) => updateField('status', v as InvoiceStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Invoice Version */}
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={formData.invoice_version ?? String(invoice.invoice_version ?? 1)}
                    onChange={(e) => updateField('invoice_version', e.target.value)}
                  />
                </div>

                {/* Invoice Number */}
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={formData.invoice_number ?? invoice.invoice_number ?? ''}
                    onChange={(e) => updateField('invoice_number', e.target.value)}
                    placeholder="Leave empty to auto-fill"
                    maxLength={4}
                    disabled={formLocked}
                  />
                  {formData.invoice_number !== undefined && !/^\d{4}$/.test(formData.invoice_number) && (
                    <p className="text-xs text-destructive">Must contain exactly 4 numeric characters</p>
                  )}
                </div>

                {/* Invoice Date */}
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input
                    type="date"
                    value={formData.date || formatDateValue(invoice.date)}
                    onChange={(e) => updateField('date', e.target.value)}
                    disabled={formLocked}
                  />
                </div>

                {/* Due Date — always disabled, auto-calculated from date + payment_terms (V1 parity) */}
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.expiration_date || formatDateValue(invoice.expiration_date)}
                    disabled
                    readOnly
                  />
                </div>

                {/* Payment Terms */}
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select
                    value={formData.payment_terms || invoice.payment_terms || 'net-30'}
                    onValueChange={(v) => updateField('payment_terms', v as PaymentTerms)}
                    disabled={formLocked}
                  >
                    <SelectTrigger disabled={formLocked}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTermsOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tax Category */}
                <div className="space-y-2">
                  <Label>Taxes</Label>
                  <Select
                    value={formData.tax_category || invoice.tax_category || 'none'}
                    onValueChange={(v) => updateField('tax_category', v as TaxCategory)}
                    disabled={formLocked}
                  >
                    <SelectTrigger disabled={formLocked}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taxCategoryOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {taxMismatch && (
                    <p className="text-xs text-warning">
                      ⚠{' '}
                      {t(
                        'warnings.taxMismatch',
                        "Expected {{taxCategory}} for this customer's province. Verify the tax category.",
                        { taxCategory: TAX_CATEGORIES[expectedTaxCategory!].label },
                      )}
                    </p>
                  )}
                </div>

                {/* Payment Date (for paid status) */}
                {((formData.status || invoice.status) === 'paid') && (
                  <div className="space-y-2">
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={formData.payment_date || formatDateValue(invoice.payment_date)}
                      onChange={(e) => updateField('payment_date', e.target.value)}
                    />
                  </div>
                )}

                {/* Payment Proof */}
                {((formData.status || invoice.status) === 'paid') && (
                  <div className="space-y-2">
                    <Label>Payment Proof</Label>
                    <Input
                      value={formData.payment_ref ?? invoice.payment_ref ?? ''}
                      onChange={(e) => updateField('payment_ref', e.target.value)}
                      placeholder="Check #, wire transfer #, etc."
                    />
                  </div>
                )}
              </div>

              <Separator />

              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Name</Label>
                  <Input
                    value={formData.project_name ?? invoice.project_name ?? ''}
                    onChange={(e) => updateField('project_name', e.target.value)}
                    disabled={formLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select
                    value={formData.project_number ?? invoice.project_number ?? '__none__'}
                    onValueChange={(v) => updateField('project_number', v === '__none__' ? '' : v)}
                    disabled={formLocked || projectsQuery.isLoading}
                  >
                    <SelectTrigger disabled={formLocked || projectsQuery.isLoading}>
                      <SelectValue
                        placeholder={
                          projectsQuery.isLoading ? 'Loading...' : 'Select project...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.projectNumber}>
                          {p.projectNumber}{p.projectName ? ` — ${p.projectName}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quote</Label>
                  <Select
                    value={formData.quote_number ?? invoice.quote_number ?? '__none__'}
                    onValueChange={(v) => updateField('quote_number', v === '__none__' ? '' : v)}
                    disabled={formLocked || quotesQuery.isLoading}
                  >
                    <SelectTrigger disabled={formLocked || quotesQuery.isLoading}>
                      <SelectValue
                        placeholder={
                          quotesQuery.isLoading ? 'Loading...' : 'Select quote...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {quotes.map((q) => (
                        <SelectItem key={q.id} value={q.quoteNumber}>
                          {q.quoteNumber}{q.projectName ? ` — ${q.projectName}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-4">
                  {/* Purchase Order file — link + detach, or upload when none is attached (QUAL-5947).
                      Mirrors the quote modal's PO-file affordance on the working files proxy. */}
                  <div className="space-y-2">
                    <Label>{t('fields.poFile', 'PO File')}</Label>
                    {invoice.po_file ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={`/api/v4/legacy/v2/files/${typeof invoice.po_file === 'object' ? (invoice.po_file as { _id: string })._id : invoice.po_file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-atek-blue hover:underline truncate"
                        >
                          {typeof invoice.po_file === 'object' && (invoice.po_file as { name?: string }).name
                            ? (invoice.po_file as { name: string }).name
                            : t('fields.poFile', 'PO File')}
                        </a>
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        {!formLocked && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            aria-label={t('fields.deletePoFile', 'Remove file')}
                            onClick={() => invoice._id && deletePoFileMutation.mutate(invoice._id)}
                            disabled={deletePoFileMutation.isPending}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ) : !formLocked ? (
                      <div>
                        <input
                          ref={poFileInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && invoice?._id) {
                              uploadPoFileMutation.mutate({ invoiceId: invoice._id, file });
                            }
                            // Reset input so the same file can be selected again
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => poFileInputRef.current?.click()}
                          disabled={uploadPoFileMutation.isPending}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          {t('fields.uploadPoFile', 'Upload PO File')}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('fields.noPoFile', 'No file attached')}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>P/O Number</Label>
                    <Input
                      value={formData.po_number ?? invoice.po_number ?? ''}
                      onChange={(e) => updateField('po_number', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Tracking & Links */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tracking Numbers</Label>
                  <Input
                    value={
                      formData.tracking_numbers?.join(', ') ??
                      invoice.tracking_numbers?.join(', ') ??
                      ''
                    }
                    onChange={(e) =>
                      updateField(
                        'tracking_numbers',
                        e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="Comma-separated tracking numbers"
                  />
                </div>
                <div className="space-y-2">
                  <Label>HubSpot Link</Label>
                  <Input
                    value={formData.hubspot_link ?? invoice.hubspot_link ?? ''}
                    onChange={(e) => updateField('hubspot_link', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Google Drive Link</Label>
                  <Input
                    value={formData.gdrive_link ?? invoice.gdrive_link ?? ''}
                    onChange={(e) => updateField('gdrive_link', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Next Steps */}
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Next Step</Label>
                  <Textarea
                    value={formData.next_step ?? invoice.next_step ?? ''}
                    onChange={(e) => updateField('next_step', e.target.value)}
                    placeholder="What needs to happen next?"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Step Deadline</Label>
                  <Input
                    type="date"
                    value={
                      formData.next_step_deadline || formatDateValue(invoice.next_step_deadline) || ''
                    }
                    onChange={(e) => updateField('next_step_deadline', e.target.value)}
                  />
                </div>
              </div>

              {/* Timestamps */}
              <div className="text-xs text-muted-foreground mt-4">
                Created: {formatDateTime(invoice.created_at)} | Updated:{' '}
                {formatDateTime(invoice.updated_at)}
              </div>
            </InvoiceFormSection>

            {/* Account */}
            <InvoiceFormSection title="Account" icon={<Building2 className="h-4 w-4" />} accent="blue">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account</Label>
                  <SearchableSelect
                    value={effectiveCustomerId || null}
                    onValueChange={(v) => handleCustomerChange(v || '')}
                    options={customers}
                    placeholder="Select account..."
                    emptyText="No accounts found."
                    disabled={formLocked}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subscription</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={effectiveContractId}
                      onValueChange={(v) => updateField('contract', v === '__none__' ? '' : v)}
                      disabled={formLocked || contractsQuery.isLoading}
                    >
                      <SelectTrigger disabled={formLocked || contractsQuery.isLoading}>
                        <SelectValue
                          placeholder={
                            contractsQuery.isLoading
                              ? 'Loading...'
                              : contracts.length === 0
                                ? 'No subscriptions'
                                : 'Select subscription...'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {contracts.map((c) => (
                          <SelectItem key={c._id} value={c._id}>
                            {getContractDisplay(c)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {mongoContractIdForFill && !formLocked && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        title="Fill from subscription"
                        aria-label="Fill from subscription"
                        data-testid="fill-from-subscription-btn"
                        disabled={contractDocQuery.isFetching}
                        onClick={() => {
                          const contract = contractDocQuery.data;
                          if (contract) {
                            fillFromContract(contract);
                          }
                        }}
                      >
                        {contractDocQuery.isFetching ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Account Manager</Label>
                  <Select
                    value={formData.sales_manager || extractId(invoice.sales_manager)}
                    onValueChange={(v) => updateField('sales_manager', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager..." />
                    </SelectTrigger>
                    <SelectContent>
                      {salesManagers
                        .filter((m) => {
                          // The invoice's sales_manager is a Mongo user id, so match the
                          // manager list (PG UUID `id` + Mongo `mongoUserId`) on BOTH id
                          // spaces — otherwise an assigned/former AM is filtered out and the
                          // field renders blank. Mirrors the quote form's manager picker.
                          const current = formData.sales_manager || extractId(invoice.sales_manager);
                          return !m.isFormer || m.mongoUserId === current || m.id === current;
                        })
                        .map((m) => (
                          <SelectItem key={m.id} value={m.mongoUserId || m.id}>
                            {m.name}{m.isFormer ? ' (former)' : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Contractual Manager</Label>
                  <Select
                    value={formData.contractual_manager ?? extractId(invoice.contractual_manager)}
                    onValueChange={(v) => updateField('contractual_manager', v === '__none__' ? '' : v)}
                    disabled={formLocked || orgUsersQuery.isLoading}
                  >
                    <SelectTrigger disabled={formLocked || orgUsersQuery.isLoading}>
                      <SelectValue
                        placeholder={
                          orgUsersQuery.isLoading
                            ? 'Loading...'
                            : 'Select manager...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {orgUsers.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {getUserName(u)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isPopulatedUser(invoice.contractual_manager) &&
                    invoice.contractual_manager.email &&
                    !formData.contractual_manager && (
                      <p className="text-xs text-muted-foreground">
                        {invoice.contractual_manager.email}
                      </p>
                    )}
                </div>
              </div>

              <Separator />

              {/* Billing Email (from the billing site) — shown above Billing Address */}
              {isPopulatedSite(invoice.billing_site) && invoice.billing_site.email && (
                <div className="space-y-2">
                  <Label>{t('fields.billingEmail', 'Billing Email')}</Label>
                  <a
                    href={`mailto:${invoice.billing_site.email}`}
                    className="block text-sm text-atek-blue hover:underline"
                  >
                    {invoice.billing_site.email}
                  </a>
                </div>
              )}

              {/* Billing Address */}
              <div className="space-y-2">
                <Label>Billing Address</Label>
                <Textarea
                  value={formData.billing_address ?? invoice.billing_address ?? ''}
                  onChange={(e) => updateField('billing_address', e.target.value)}
                  rows={3}
                  disabled={formLocked}
                />
              </div>

              {/* Shipping Addresses */}
              {invoice.shipping_addresses && invoice.shipping_addresses.length > 0 && (
                <div className="space-y-2">
                  <Label>Shipping Addresses</Label>
                  {invoice.shipping_addresses.map((addr, idx) => (
                    <div key={idx} className="p-2 bg-muted rounded text-sm">
                      {addr.address || '-'}
                    </div>
                  ))}
                </div>
              )}

              {/* Language & Currency */}
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select
                    value={formData.language || invoice.language || 'fr'}
                    onValueChange={(v) => updateField('language', v as Language)}
                    disabled={formLocked}
                  >
                    <SelectTrigger disabled={formLocked}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency || invoice.currency || 'CAD'}
                    onValueChange={(v) => updateField('currency', v as Currency)}
                    disabled={formLocked}
                  >
                    <SelectTrigger disabled={formLocked}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </InvoiceFormSection>

            {/* Items */}
            <InvoiceFormSection title="Items" icon={<Package className="h-4 w-4" />} accent="indigo">
              {/* Editable SKUs Table */}
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full min-w-[1112px] table-fixed text-sm">
                  <colgroup>
                    <col className="w-[280px]" />
                    <col className="w-[320px]" />
                    <col className="w-24" />
                    <col className="w-32" />
                    <col className="w-24" />
                    <col className="w-36" />
                    <col className="w-12" />
                  </colgroup>
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 whitespace-nowrap">SKU</th>
                      <th className="text-left p-2 whitespace-nowrap">Description</th>
                      <th className="text-right p-2 whitespace-nowrap">Qty</th>
                      <th className="text-right p-2 whitespace-nowrap">Unit Price</th>
                      <th className="text-right p-2 whitespace-nowrap">Disc %</th>
                      <th className="text-right p-2 whitespace-nowrap">Total</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveSkus.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted-foreground py-8">
                          No line items. Click "Add Row" to add one.
                        </td>
                      </tr>
                    ) : (
                      effectiveSkus.map((sku, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">
                            <SearchableSelect
                              value={sku.sku || null}
                              onValueChange={(v) => handleSkuSelect(idx, v)}
                              options={skuOptions}
                              placeholder="Select SKU..."
                              emptyText="No SKUs found."
                              className="h-8 text-xs"
                              disabled={formLocked}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              value={sku.description || ''}
                              onChange={(e) => updateSku(idx, { description: e.target.value })}
                              className="h-8 text-sm"
                              placeholder="Description"
                              disabled={formLocked}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              aria-label={`Quantity for item ${idx + 1}`}
                              min={0}
                              value={sku.quantity}
                              onChange={(e) => updateSku(idx, { quantity: Number(e.target.value) })}
                              className="h-8 min-w-[72px] text-sm text-right"
                              disabled={formLocked}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              value={sku.unit_price}
                              onChange={(e) => updateSku(idx, { unit_price: Number(e.target.value) })}
                              className="h-8 min-w-[96px] text-sm text-right"
                              disabled={formLocked}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={sku.discount || 0}
                              onChange={(e) => updateSku(idx, { discount: Number(e.target.value) })}
                              className="h-8 min-w-[72px] text-sm text-right"
                              disabled={formLocked}
                            />
                          </td>
                          <td className="p-2 text-right font-mono">
                            {formatCurrency(calculateLineTotal(sku.quantity, sku.unit_price, sku.discount), effectiveCurrency)}
                          </td>
                          <td className="p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => removeSku(idx)}
                              title="Remove row"
                              disabled={formLocked}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Add Row & Totals */}
              <div className="flex items-center justify-between">
                <Button type="button" variant="outline" size="sm" onClick={addSku} disabled={formLocked}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Row
                </Button>

                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-mono">{formatCurrency(subtotal, effectiveCurrency)}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Tax ({effectiveTaxCategory}):
                      </span>
                      <span className="font-mono">{formatCurrency(taxAmount, effectiveCurrency)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Total:</span>
                    <span className="font-mono">{formatCurrency(totalWithTax, effectiveCurrency)}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <Separator />
              <div className="space-y-2">
                <Label>Description (shown on invoice)</Label>
                <Textarea
                  value={formData.description ?? invoice.description ?? ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  disabled={formLocked}
                />
              </div>
            </InvoiceFormSection>

            {/* Sensors */}
            <InvoiceFormSection title="Sensors" icon={<Radio className="h-4 w-4" />} accent="teal">
              <div className="space-y-3">
                {!invoiceNumber ? (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    Save the invoice first to link sensors.
                  </p>
                ) : (
                  <>
                    <InvoiceSensorsTable
                      sensors={sensors}
                      isLoading={sensorsLoading}
                      onRemoveSensor={handleRemoveSensor}
                      onRemoveAllSensors={handleRemoveAllSensors}
                      isRemoving={removeSensorMutation.isPending}
                      disabled={formLocked}
                    />
                    {!formLocked && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSensorDialogOpen(true)}
                        disabled={!effectiveCustomerId}
                        title={!effectiveCustomerId ? 'Select an account first' : 'Add sensors'}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add sensors
                      </Button>
                    )}
                  </>
                )}
              </div>
            </InvoiceFormSection>

            {/* Notes */}
            <InvoiceFormSection title="Notes" icon={<MessageSquare className="h-4 w-4" />} accent="slate">
              {/* Internal Notes */}
              <div className="space-y-2">
                <Label>Internal Notes (not shown on invoice)</Label>
                <Textarea
                  value={formData.internal_notes ?? invoice.internal_notes ?? ''}
                  onChange={(e) => updateField('internal_notes', e.target.value)}
                  rows={4}
                  placeholder="Internal notes visible only to staff..."
                />
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label>Comment</Label>
                <Textarea
                  value={formData.comment ?? invoice.comment ?? ''}
                  onChange={(e) => updateField('comment', e.target.value)}
                  rows={2}
                />
              </div>

              <Separator />

              {/* Notes History */}
              <div className="space-y-2">
                <Label>Notes History</Label>
                {invoice.notes && invoice.notes.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {invoice.notes.map((note, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded text-sm">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>{note.user_name || 'Unknown'}</span>
                          <span>{formatDateTime(note.date)}</span>
                        </div>
                        <p className="whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-4 text-sm">
                    No notes yet
                  </div>
                )}
                {!!invoice._id && (
                  <div className="space-y-2 pt-2">
                    <Textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!noteDraft.trim() || addNoteMutation.isPending}
                      onClick={() => {
                        // Note: do NOT send `user`. useSession()._id is the PG UUID,
                        // but mongo_server types notes[].user as an ObjectId, so a UUID
                        // fails Mongoose casting and 500s. user_name (a string) is enough
                        // for the Notes History display.
                        void addNoteMutation
                          .mutateAsync({
                            invoiceId: invoice._id,
                            data: {
                              content: noteDraft.trim(),
                              date: new Date().toISOString(),
                              user_name: `${sessionUser?.firstname ?? ''} ${sessionUser?.lastname ?? ''}`.trim(),
                            },
                          })
                          .then(() => setNoteDraft(''))
                          .catch(() => {
                            /* useAddInvoiceNote.onError already toasts */
                          });
                      }}
                    >
                      Add Note
                    </Button>
                  </div>
                )}
              </div>
            </InvoiceFormSection>

            {/* Documents */}
            <InvoiceFormSection title="Documents" icon={<Paperclip className="h-4 w-4" />} accent="amber">
              <InvoiceDocumentsTab invoiceId={invoice.pgId || invoice._id} />
            </InvoiceFormSection>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {hasChanges ? 'Cancel' : 'Close'}
          </Button>
          {hasChanges && (
            <Button onClick={handleSubmit} disabled={isSubmitting || !invoiceNumberValid}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Sensor picker — rendered outside DialogContent to avoid z-index stacking issues */}
      {sensorDialogOpen && (
        <SensorSelectDialog
          open={sensorDialogOpen}
          onOpenChange={setSensorDialogOpen}
          customerId={mongoOrgId || effectiveCustomerId}
          pgAccountId={effectiveCustomerId || undefined}
          quoteSensorIds={linkedSensorIds}
          onAddSensor={handleAddSensor}
          onRemoveSensor={handleRemoveSensor}
        />
      )}
    </Dialog>
  );
}
